// src/encounter/rail-v3/rail-v3-column.tsx
// The left patient summary rail (`railV3`, ON on dev), built from Antonin's catalogue (left-rail target 4 Nov 2026 §5): the sections,
// rows, hover bar, hover card, inline note and medication card are @scripsteam/signal components
// (PatientRailSection, PatientRailRow, PatientRailIconButton, useRailHover / RailRowHoverCard, RailNote,
// MedicationCard), rendered inside `SignalScope`, the app's one catalogue scope. This file is an ADAPTER only: it maps the rail's
// sections (`patient-summary-rail.tsx`, the same props as `ChartColumnV2`) onto those parts and attaches the rail's handlers. It writes
// no look: no colour, border, radius, shadow, type class or inline style; the only classes are layout glue.
//
// What comes from where:
//   · order, titles, empty copy, fold, row numbers, card fields: rail-v3-model.ts (unchanged rules)
//   · which actions, per stage and persona: rail-v3-actions.ts + patient-summary-rail.tsx (unchanged); their catalogue form: bar-actions.ts
//   · an action with a surface (an edit form, the "Entered in error" reason) opens it anchored to its row (the rail's
//     RailAnchoredSurface), as before; "Add to note" (started) and "Add a note" (pre-visit) open the catalogue RailNote IN the row, and
//     its confirm (Ctrl/Cmd+Enter) calls the row handlers (`rowHandlers.addToNote` / `addPrevisitNote`).
//   · a medication row opens the catalogue MedicationCard (its details and every action, Stop red and last); its bar is the card's
//     non-destructive bar actions.
import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Skeleton, type IpsAction, type IpsEntry } from '@scripsteam/scrips-signal-ds';
import {
  MedicationCard,
  OverlayHost,
  PatientRailIconButton,
  PatientRailRow,
  PatientRailSection,
  RailNote,
  barActs,
  useRailHover,
  type CardAction,
  type CardData,
} from '@scripsteam/signal';
import { useRole } from '@/auth/use-role';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
// The person card is still the app-drawn one from the legacy column (a gap: no catalogue part for it yet).
import { ChartColumnPersonCard } from '../chart-column-v2';
import type { ChartColumnSection, ChartRailProps } from '../lib/chart-rail-types';
import { realEntries } from '../lib/chart-column-model';
import { RailAnchoredSurface, type RailSurfaceRender } from '../pre-encounter-parts';
import { SignalScope } from '../nurse-med-rec/nurse-med-rec-block';
import { RAIL_V3_ACTION_ICON, railV3CardActions, railV3DangerLast } from './bar-actions';
import { RAIL_NOTE_SECTION_KIND, soleTarget, type RailNoteTarget, type RailNoteTargetId } from './rail-note-targets';
import { NO_NOTE_REASON } from '../lib/rail-note-piece';
import {
  RAIL_V3_SENTENCE_CASE,
  railV3ActionWord,
  railV3ActionsFor,
  railV3AddLabel,
  railV3CardInfo,
  railV3DefaultOpen,
  railV3DeviceDataAt,
  railV3EmptyCopy,
  railV3OrderSections,
  railV3RowEnd,
  railV3RowParts,
  railV3SentenceCase,
  railV3ShowCount,
  railV3Title,
  type RailV3Stage,
} from './rail-v3-model';
import { RailV3Contacts } from './rail-v3-contacts';

export type { RailV3Stage } from './rail-v3-model';

export interface RailV3ColumnProps extends ChartRailProps {
  stage: RailV3Stage;
  /** The summary read failed: the panel says so (§7) and offers `onRetry`. `loadError` (a node) wins when given. */
  loadFailed?: boolean;
  /** The host's retry for `loadFailed` (the existing summary refetch). */
  onRetry?: () => void;
}

/** What an open surface shows. Only ids are kept: the surface's content is read from the CURRENT sections on every render (an add or
 *  edit form's search state lives in the rail and changes while it is open; a stored render would freeze it — Codex, 4 Nov). */
type SurfaceTarget =
  | { kind: 'add'; sectionId: string }
  | { kind: 'action'; sectionId: string; entryId: string; actionLabel: string }
  | { kind: 'medication'; sectionId: string; entryId: string };

/** One anchored surface at a time (an add form, a row action's form, the medication card), as the catalogue rail keeps one popover. */
interface OpenSurface {
  /** `add:<section>` or `row:<section>:<entry>` */
  key: string;
  label: string;
  anchor: HTMLElement;
  target: SurfaceTarget;
  seq: number;
}

/** The inline note open on one row: the started visit's "Add to note", or the pre-visit's "Add a note". */
interface OpenNote {
  key: string;
  kind: 'encounter' | 'previsit';
  /** The target the clinician picked in the hover card (§4b round 4); absent = a medication's sole target or no pick yet. */
  targetId?: RailNoteTargetId;
}

type Open = (s: Omit<OpenSurface, 'seq'>) => void;

const rowKey = (sectionId: string, entryId: string) => `row:${sectionId}:${entryId}`;

/** §7: a skeleton in the panel's shape: a title line over two rows, per section. (The catalogue's loading state is not exported.) */
export function RailV3Loading({ count }: { count: number }) {
  return (
    <div role="status" aria-busy="true" data-slot="rail-skeleton" className="flex flex-col">
      <span className="sr-only">Loading patient summary…</span>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} aria-hidden="true" data-slot="rail-skeleton-section" className="flex flex-col gap-2 p-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      ))}
    </div>
  );
}

/** §7: the read failed; this is not an empty record. (The catalogue's error state is not exported.) */
export function RailV3LoadError({ onRetry }: { onRetry?: () => void }) {
  return (
    <div role="alert" data-slot="rail-load-error" className="flex flex-col items-start gap-2 p-4">
      <p data-slot="rail-error-title" className="m-0">
        Patient summary couldn't be loaded
      </p>
      <p className="m-0">Allergies, problems and medications are not shown. This is not an empty record.</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

interface RowCtx {
  stage: RailV3Stage;
  canResolve: boolean;
  sentenceCase: boolean;
  hover: ReturnType<typeof useRailHover>;
  surface: OpenSurface | null;
  open: Open;
  note: OpenNote | null;
  setNote: (n: OpenNote | null) => void;
}

/** Runs a row action as the rail does: "Add to note" / "Add a note" open the row's RailNote when the row has their handler; an action
 *  with a surface opens it anchored to the row; any other runs its own handler. */
function runAction(model: ChartColumnSection, entry: IpsEntry, action: IpsAction, anchor: HTMLElement | null, ctx: RowCtx) {
  const key = rowKey(model.section.id, entry.id);
  const handlers = model.rowHandlers;
  if (action.label === 'Add to note' && handlers?.addToNote) {
    const offered = handlers.targetsFor?.(entry.id);
    if (!offered || offered.targets.length === 0) {
      toast(offered?.reason ?? NO_NOTE_REASON);
      return;
    }
    ctx.setNote({ key, kind: 'encounter' });
    return;
  }
  if (action.label === '+ Note' && handlers?.addPrevisitNote) {
    ctx.setNote({ key, kind: 'previsit' });
    return;
  }
  if (anchor && model.actionPopover?.(entry.id, action.label)) {
    model.actionOpenChange?.(entry.id, action.label)?.(true);
    ctx.open({
      key,
      label: railV3ActionWord(action.label),
      anchor,
      target: { kind: 'action', sectionId: model.section.id, entryId: entry.id, actionLabel: action.label },
    });
    return;
  }
  action.onClick();
}

/** A row's actions in the catalogue's form, run through the rail (`runAction`) against the row element. */
function rowActs(model: ChartColumnSection, entry: IpsEntry, anchor: HTMLElement | null, ctx: RowCtx): CardAction[] {
  const shown = railV3ActionsFor(model, entry, { canResolve: ctx.canResolve });
  return railV3CardActions(shown.actions, { run: (a) => runAction(model, entry, a, anchor, ctx), blocked: (reason) => toast(reason) });
}

/** The row's offered note targets for the clinician to pick: a problem or an allergy, even with ONE target left (UC-30 rule 2: the
 *  pick is always theirs; Codex, second pass on #961). A medication has its sole target (HPI) and keeps the direct note. */
function pickTargets(model: ChartColumnSection, entry: IpsEntry): readonly RailNoteTarget[] | undefined {
  const kind = RAIL_NOTE_SECTION_KIND[model.section.id];
  if (!kind || kind === 'medication' || !model.rowHandlers?.addToNote) return undefined;
  const offered = model.rowHandlers.targetsFor?.(entry.id);
  return offered && offered.targets.length > 0 ? offered.targets : undefined;
}

/** The hover card's actions for a row with several note targets: "Add to note" becomes one action per target, in Elation's order
 *  (Claude Design rounds 2 and 4: "one action per target"). A target that writes only the diagnoses runs at once (no text); any
 *  other opens the row's note aimed at it. No target is picked for the clinician (UC-30 rule 2). */
function cardActsWithTargets(acts: CardAction[], targets: readonly RailNoteTarget[], entry: IpsEntry, model: ChartColumnSection, ctx: RowCtx): CardAction[] {
  const at = acts.findIndex((a) => a.label === railV3ActionWord('Add to note'));
  if (at < 0) return acts;
  const key = rowKey(model.section.id, entry.id);
  const perTarget: CardAction[] = targets.map((t) => {
    const codeOnly = t.lands.every((l) => l === 'diagnoses');
    return {
      label: t.label,
      Icon: codeOnly ? RAIL_V3_ACTION_ICON['Add to encounter diagnoses'] : RAIL_V3_ACTION_ICON['Add to note'],
      bar: false,
      onSelect: codeOnly ? () => model.rowHandlers?.addToNote?.(entry.id, t.id) : () => ctx.setNote({ key, kind: 'encounter', targetId: t.id }),
    };
  });
  // A diagnoses target replaces the separate "Add to encounter diagnoses" action (round 4: "now the fifth target, not a second action").
  const dxWord = railV3ActionWord('Add to encounter diagnoses');
  const hasDx = targets.some((t) => t.lands.includes('diagnoses'));
  const rest = (xs: CardAction[]) => (hasDx ? xs.filter((a) => a.label !== dxWord) : xs);
  return [...rest(acts.slice(0, at)), ...perTarget, ...rest(acts.slice(at + 1))];
}

/** The catalogue MedicationCard for a medication row: the record's fields and every action, the destructive one last. */
function medicationCard(model: ChartColumnSection, entry: IpsEntry, anchor: HTMLElement, ctx: RowCtx): RailSurfaceRender {
  const info = railV3CardInfo(model.section.id, entry, model.rowExtras?.(entry.id));
  const acts = railV3DangerLast(rowActs(model, entry, anchor, ctx));
  return ({ close }) => (
    <SignalScope slot="rail-medication-card">
      <MedicationCard
        title={info.name}
        kind={info.kind}
        fields={info.fields}
        actions={acts.map((a) => ({
          ...a,
          onSelect: () => {
            close();
            a.onSelect?.();
          },
        }))}
      />
    </SignalScope>
  );
}

/** The open surface's content and its open-change hook, from the current sections (undefined when its row or form is gone). */
function resolveSurface(
  sections: readonly ChartColumnSection[],
  s: OpenSurface,
  ctx: RowCtx,
): { render: RailSurfaceRender; onOpenChange?: (open: boolean) => void } | undefined {
  const t = s.target;
  const model = sections.find((m) => m.section.id === t.sectionId);
  if (!model) return undefined;
  if (t.kind === 'add') return model.addPopover ? { render: model.addPopover, onOpenChange: model.onAddOpenChange } : undefined;
  if (t.kind === 'action') {
    const render = model.actionPopover?.(t.entryId, t.actionLabel);
    return render ? { render, onOpenChange: model.actionOpenChange?.(t.entryId, t.actionLabel) } : undefined;
  }
  const entry = realEntries(model.section).find((e) => e.id === t.entryId);
  return entry ? { render: medicationCard(model, entry, s.anchor, ctx) } : undefined;
}

function RailV3Row({ model, entry, ctx }: { model: ChartColumnSection; entry: IpsEntry; ctx: RowCtx }) {
  const sectionId = model.section.id;
  const key = rowKey(sectionId, entry.id);
  const [el, setEl] = useState<HTMLLIElement | null>(null);
  const extras = model.rowExtras?.(entry.id);
  const parts = railV3RowParts(sectionId, entry, extras);
  const end = railV3RowEnd(parts);
  const readOnly = ctx.stage === 'record';
  // On the read-only record panel the catalogue row itself draws no bar and a card with no actions (`readOnly`).
  const acts = rowActs(model, entry, el, ctx);
  const info = railV3CardInfo(sectionId, entry, extras);
  const name = ctx.sentenceCase && RAIL_V3_SENTENCE_CASE.includes(sectionId) ? railV3SentenceCase(entry.primary) : entry.primary;
  const noteOpen = ctx.note?.key === key ? ctx.note : null;
  const note = noteOpen ? (
    <RailNote
      label={`Note — ${entry.primary}`}
      onCancel={() => ctx.setNote(null)}
      onConfirm={(text) => {
        const typed = text.trim();
        if (noteOpen.kind === 'encounter') {
          // The target (§4b round 4): a medication's one target at once; any other row waits for the clinician's pick in the
          // catalogue's target list (§7.1, being added upstream). No default target (UC-30 rule 2): with no pick, nothing is
          // written and the note stays open.
          const kind = RAIL_NOTE_SECTION_KIND[sectionId];
          const offered = model.rowHandlers?.targetsFor?.(entry.id);
          const picked = noteOpen.targetId && offered?.targets.some((t) => t.id === noteOpen.targetId) ? noteOpen.targetId : undefined;
          const chosen = picked ?? (kind && offered ? soleTarget({ kind }, offered)?.id : undefined);
          if (!chosen) return;
          model.rowHandlers?.addToNote?.(entry.id, chosen, typed || undefined);
        } else {
          // Pre-visit: an empty note sends nothing (the record's note is appended, never cleared).
          if (!typed) return;
          model.rowHandlers?.addPrevisitNote?.(entry.id, typed);
        }
        ctx.setNote(null);
      }}
    />
  ) : undefined;
  const common = {
    row: entry.id,
    title: entry.primary,
    name,
    tone: sectionId === 'allergies' ? ('strong' as const) : sectionId === 'resolved-problems' ? ('muted' as const) : undefined,
    detail: parts.inline,
    meta: end.meta,
    ...(end.mark ? { mark: end.mark } : {}),
    readOnly,
    suspended: !!ctx.surface,
    open: ctx.surface?.key === key || undefined,
    note,
    wrap: !!noteOpen,
    rowRef: setEl,
  };
  // The catalogue's medication row: no hover card; its bar is the card's non-destructive actions; the row opens the MedicationCard.
  // On the read-only record panel the catalogue row opens nothing, so the details come in the hover card there (no actions).
  if (sectionId === 'medications' && !readOnly) {
    const openCard = (anchor: HTMLElement) =>
      ctx.open({ key, label: info.name, anchor, target: { kind: 'medication', sectionId, entryId: entry.id } });
    return <PatientRailRow {...common} bar={barActs(acts)} onOpen={openCard} />;
  }
  const targets = readOnly ? undefined : pickTargets(model, entry);
  if (!targets) {
    const card: CardData = { id: key, title: info.name, kind: info.kind ?? '', code: info.code, fields: info.fields, acts };
    return <PatientRailRow {...common} card={card} hover={ctx.hover} />;
  }
  // Several targets: the card lists them; the bar keeps "Add to note", which opens that card with its first action focused (the
  // pick list, until the catalogue's RailNote carries one, §7.1).
  const card: CardData = { id: key, title: info.name, kind: info.kind ?? '', code: info.code, fields: info.fields, acts: cardActsWithTargets(acts, targets, entry, model, ctx) };
  const bar = barActs(acts).map((a) =>
    a.label === railV3ActionWord('Add to note') && el ? { ...a, onSelect: () => ctx.hover.setCard({ data: card, at: el.getBoundingClientRect(), focus: true }) } : a,
  );
  return <PatientRailRow {...common} card={card} bar={bar} hover={ctx.hover} />;
}

function RailV3Section({ model, ctx }: { model: ChartColumnSection; ctx: RowCtx }) {
  const { section } = model;
  const entries = realEntries(section);
  const title = railV3Title(section);
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override ?? railV3DefaultOpen(section.id, model.unavailable);
  const canAdd = ctx.stage !== 'record' && section.id !== 'resolved-problems' && (!!model.addPopover || !!section.onAdd);
  const addLabel = railV3AddLabel(section.id) ?? model.addLabel ?? `Add to ${section.title}`;
  const addKey = `add:${section.id}`;
  // A single-record section (Occupation, Education) reads "Edit" once filled (§3): the pencil, not the plus.
  const AddIcon = model.filled ? Pencil : Plus;
  const add = canAdd ? (
    <PatientRailIconButton
      name={addLabel}
      active={ctx.surface?.key === addKey}
      onClick={(e) => {
        if (!model.addPopover) {
          section.onAdd?.();
          return;
        }
        model.onAddOpenChange?.(true);
        ctx.open({ key: addKey, label: addLabel, anchor: e.currentTarget, target: { kind: 'add', sectionId: section.id } });
      }}
    >
      <AddIcon aria-hidden />
    </PatientRailIconButton>
  ) : null;
  const emptyLine = model.unavailable ? model.emptyLabel : entries.length === 0 ? railV3EmptyCopy(section.id) : null;
  // The catalogue's wide list (PatientContextRail `.list`, space-8) is Allergies, Problem list and Resolved problems; every other
  // section is its tight list (`.listTight`, space-4).
  const wide = section.id === 'allergies' || section.id === 'active-problems' || section.id === 'resolved-problems';
  return (
    <PatientRailSection
      title={title}
      count={railV3ShowCount(entries.length, model.unavailable) ? entries.length : undefined}
      open={open}
      onOpenChange={setOverride}
      emptyLine={emptyLine}
      actions={add}
    >
      {/* Layout glue only: the catalogue's row list is not exported (a gap); the rows are its PatientRailRow. */}
      <ul aria-label={title} className={wide ? 'flex flex-col gap-2' : 'flex flex-col gap-1'}>
        {entries.map((e) => (
          <RailV3Row key={e.id} model={model} entry={e} ctx={ctx} />
        ))}
      </ul>
    </PatientRailSection>
  );
}

/** The rail. Same props as `ChartColumnV2`, plus the stage. */
export function RailV3Column({ person, sections, loading, loadError, loader, children, deviceData, stage, loadFailed, onRetry }: RailV3ColumnProps) {
  const { isNurse } = useRole();
  // `?x=rail-fixes` (PROD-3383/3384/3385/3386): no patient card on top and the medical-term sentence case; the page layout hooks on
  // `data-rail-fixes`. Read once per mount.
  const [railFixes] = useState(() => isEncounterFlagOn('railFixes'));
  // Only where the page header already carries the identity and the status: the pre-visit's one header, or the started visit (whose
  // card draws no status). Anywhere else the card still holds something only it shows, so it stays.
  const dropCard = railFixes && (!!person.identityInHeader || (stage === 'started' && !person.statusSlot && !person.status));
  const hover = useRailHover();
  const [surface, setSurface] = useState<OpenSurface | null>(null);
  const [note, setNote] = useState<OpenNote | null>(null);
  // The hover card's layer: inside the catalogue scope (its tokens), a fixed zero-width strip at the viewport's left edge, so the
  // card's own viewport coordinates hold and no scrolling column clips it.
  const [overlay, setOverlay] = useState<HTMLElement | null>(null);
  // A new surface always mounts fresh (closing one and opening the next in the same tick must not reuse the closed instance).
  const [opened, setOpened] = useState(0);
  const open: Open = (s) => {
    hover.close();
    setNote(null);
    setOpened((n) => n + 1);
    setSurface({ ...s, seq: opened + 1 });
  };
  const ctx: RowCtx = { stage, canResolve: !isNurse, sentenceCase: railFixes, hover, surface, open, note, setNote };
  const shownSurface = surface ? resolveSurface(sections, surface, ctx) : undefined;
  const ordered = railV3OrderSections(sections);
  const deviceDataAt = railV3DeviceDataAt(ordered);
  const hasContacts = !!person.contacts && person.contacts.length > 0;
  const section = (m: ChartColumnSection): ReactNode => <RailV3Section key={m.section.id} model={m} ctx={ctx} />;
  const layoutFixesStarted = stage === 'started' && isEncounterFlagOn('layoutFixes');
  return (
    <aside
      aria-label="Patient summary"
      data-region="chart"
      data-slot="chart-column"
      data-rail="v3"
      data-stage={stage}
      // `?x=previsit-header` (the pre-visit's one header): the rail also scrolls on its own there (showcase-bridge.css, page layout).
      data-previsit-header={person.identityInHeader ? 'on' : undefined}
      data-rail-fixes={railFixes ? 'on' : undefined}
      // `layoutFixes` (Antonin, 4 Nov: the left rail's divider "sometimes doesn't draw"): in the started visit the column draws its
      // divider as an inset shadow, and this rail, as wide as the column with its own white, painted over it. The started rail draws
      // the hairline itself (the pre-visit rail already has its border).
      className={
        layoutFixesStarted
          ? 'flex flex-col bg-surface-card font-sans border-r border-border-default' // hand-drawn-allow: the panel's existing background, unchanged from origin/main
          : 'flex flex-col bg-surface-card font-sans' // hand-drawn-allow: the panel's existing background, unchanged from origin/main
      }
    >
      {dropCard ? null : <ChartColumnPersonCard {...person} contacts={undefined} />}
      {loading ? (
        (loader ?? <RailV3Loading count={Math.max(sections.length, 4)} />)
      ) : loadError ? (
        loadError
      ) : loadFailed ? (
        <RailV3LoadError onRetry={onRetry} />
      ) : (
        <SignalScope slot="chart-sections">
          {/* §1: Elation's order. The contacts (its demographics block) first, folded; then the clinical sections. */}
          {hasContacts ? <RailV3Contacts contacts={person.contacts} defaultOpen={railV3DefaultOpen('contacts')} /> : null}
          {ordered.slice(0, deviceDataAt).map(section)}
          {deviceData}
          {ordered.slice(deviceDataAt).map(section)}
        </SignalScope>
      )}
      {createPortal(
        <SignalScope slot="rail-v3-overlay">
          <div ref={setOverlay} className="fixed left-0 top-0 z-50 h-screen w-0" />
        </SignalScope>,
        document.body,
      )}
      {overlay ? <OverlayHost.Provider value={overlay}>{hover.view}</OverlayHost.Provider> : null}
      {surface && shownSurface ? (
        <RailAnchoredSurface
          key={surface.seq}
          label={surface.label}
          anchor={surface.anchor}
          render={shownSurface.render}
          onOpenChange={(isOpen) => {
            shownSurface.onOpenChange?.(isOpen);
            if (!isOpen) setSurface((cur) => (cur?.seq === surface.seq ? null : cur));
          }}
        />
      ) : null}
      {children}
    </aside>
  );
}
